import { Helmet } from "react-helmet-async";

const SITE_NAME = "The Mogul Group Advisors";
const DEFAULT_DESCRIPTION =
  "Dallas-based business consulting, digital marketing, web design, branding, and operations support for startups and growing businesses across the DFW Metroplex.";
const DEFAULT_OG_IMAGE =
  "https://images.unsplash.com/photo-1573164574572-cb89e39749b4?w=1200&h=630&fit=crop&q=80";

/**
 * Reusable per-page SEO block. Sets title, description, canonical, Open Graph
 * and (optionally) additional JSON-LD structured data.
 */
export default function Seo({
  title,
  description = DEFAULT_DESCRIPTION,
  path = "/",
  image = DEFAULT_OG_IMAGE,
  jsonLd = null,
  noindex = false,
}) {
  const fullTitle = title ? `${title} · ${SITE_NAME}` : `${SITE_NAME} — Dallas Business Consulting`;
  const origin = typeof window !== "undefined" ? window.location.origin : "https://www.themoguladvisors.com";
  const canonical = `${origin}${path}`;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonical} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}

      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={image} />
      <meta property="og:locale" content="en_US" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {jsonLd && (
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      )}
    </Helmet>
  );
}

export const ORGANIZATION_LD = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: SITE_NAME,
  legalName: "The Mogul Group, LLC",
  url: "https://www.themoguladvisors.com",
  logo: "https://customer-assets-v7afamib.emergentagent.net/job_9e2c448a-18b6-4072-a034-48f10e8b6302/artifacts/xaazidaz_TMG%20WHITE%20LOGO.PNG",
  email: "info@TheMogulAdvisors.com",
  telephone: "+1-817-799-7274",
  address: {
    "@type": "PostalAddress",
    streetAddress: "539 W. Commerce St #8101",
    addressLocality: "Dallas",
    addressRegion: "TX",
    postalCode: "75208",
    addressCountry: "US",
  },
};

export const LOCAL_BUSINESS_LD = {
  "@context": "https://schema.org",
  "@type": ["LocalBusiness", "ProfessionalService"],
  "@id": "https://www.themoguladvisors.com/#organization",
  name: SITE_NAME,
  image: "https://customer-assets-v7afamib.emergentagent.net/job_9e2c448a-18b6-4072-a034-48f10e8b6302/artifacts/xaazidaz_TMG%20WHITE%20LOGO.PNG",
  url: "https://www.themoguladvisors.com",
  telephone: "+1-817-799-7274",
  email: "info@TheMogulAdvisors.com",
  priceRange: "$$",
  address: {
    "@type": "PostalAddress",
    streetAddress: "539 W. Commerce St #8101",
    addressLocality: "Dallas",
    addressRegion: "TX",
    postalCode: "75208",
    addressCountry: "US",
  },
  geo: { "@type": "GeoCoordinates", latitude: 32.7767, longitude: -96.7970 },
  areaServed: [
    { "@type": "City", name: "Dallas" },
    { "@type": "City", name: "Fort Worth" },
    { "@type": "AdministrativeArea", name: "Dallas–Fort Worth Metroplex" },
  ],
  openingHoursSpecification: [
    {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
      opens: "09:00",
      closes: "18:00",
    },
  ],
  sameAs: [],
};

export const SERVICE_CATALOG_LD = {
  "@context": "https://schema.org",
  "@type": "Service",
  provider: { "@id": "https://www.themoguladvisors.com/#organization" },
  areaServed: "Dallas–Fort Worth Metroplex",
  serviceType: "Business Consulting",
  hasOfferCatalog: {
    "@type": "OfferCatalog",
    name: "Business Consulting & Digital Solutions",
    itemListElement: [
      { "@type": "Offer", itemOffered: { "@type": "Service", name: "Business Growth Consultation" } },
      { "@type": "Offer", itemOffered: { "@type": "Service", name: "Digital Marketing" } },
      { "@type": "Offer", itemOffered: { "@type": "Service", name: "Website Design & Development" } },
      { "@type": "Offer", itemOffered: { "@type": "Service", name: "Social Media Management" } },
      { "@type": "Offer", itemOffered: { "@type": "Service", name: "Operations Consulting" } },
      { "@type": "Offer", itemOffered: { "@type": "Service", name: "Branding & Business Launch" } },
      { "@type": "Offer", itemOffered: { "@type": "Service", name: "Government & Proposal Support" } },
    ],
  },
};
